/**
 * 系统健康透视 — the five reads behind the page, folded into one state.
 *
 * ── The dependency chain, and why it is a chain ──
 *
 *   1. traffic overview           — independent, drives everything
 *   2. sku-economics              — needs (1)'s echoed `[from, to)` bounds
 *   3. revenue by-upstream        — needs (1)'s echoed `[from, to)` bounds
 *   4. routing configs            — independent
 *   5. routing config sku-summary — needs (4)'s global-default config id
 *   6. upstreams                  — independent; supplier NAMES only
 *
 * (2) and (3) are driven with `window=custom` and the exact bounds the traffic
 * response echoed back, rather than with their own named preset. A named
 * preset on each side would resolve independently — the money endpoints bucket
 * in UTC+8 business time, the traffic endpoint does not — and the page would
 * silently overlay two different intervals while every number in it looked
 * plausible. That is also why they stay DISABLED until the bounds exist: a
 * request fired with a guessed window is worse than no request.
 *
 * ── Degradation ──
 *
 * Only the traffic slice is load-bearing. Losing the money layer must not take
 * the health map down with it, so (2), (3), (4) and (5) each record a
 * `SliceFailure` and the model renders their fields as unknown. `error` is set
 * from the traffic query alone, which is the contract `PerspectiveState`
 * states and what the page's error surface keys on.
 *
 * ── Why these use `useQuery` directly ──
 *
 * `useBusinessPage` and `useRevenueByUpstream` take no `enabled` flag, and
 * conditional gating is exactly what this page needs. Rather than widen two
 * shared hooks other pages depend on, this file calls `useQuery` with THEIR
 * exported query-key builders and THEIR exported fetchers — same cache slots,
 * same URL construction, no second copy of either.
 */

import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BUSINESS_QUERY_KEYS,
  getBusinessPage,
  type BusinessQuery,
} from '../../api/business.ts';
import {
  REVENUE_QUERY_KEYS,
  getRevenueByUpstream,
  type RevenueByUpstreamQuery,
} from '../../api/revenue.ts';
import { useOpsTrafficOverview } from '../../api/traffic.ts';
import { useRoutingConfigSkuSummary, useRoutingConfigs } from '../../api/useTenantRouting.ts';
import { useUpstreamsList } from '../../api/useUpstreams.ts';
import { useSession } from '../../hooks/useSession.tsx';
import { buildPerspectiveModel } from './model.ts';
import type { PerspectiveModel, PerspectiveWindow, SliceFailure } from './types.ts';

export interface PerspectiveState {
  readonly model: PerspectiveModel | null;
  /** True only on the very first load, while no model exists yet. */
  readonly isLoading: boolean;
  /** True during any background refetch, including the auto-refresh poll. */
  readonly isFetching: boolean;
  /** Non-null ONLY when the traffic slice itself failed. */
  readonly error: Error | null;
  readonly refetch: () => void;
}

export interface UseHealthPerspectiveOptions {
  readonly autoRefresh: boolean;
}

function failureFrom(slice: SliceFailure['slice'], error: Error | null): SliceFailure | null {
  if (error === null) return null;
  return { slice, message: error.message };
}

export function useHealthPerspective(
  window: PerspectiveWindow,
  options: UseHealthPerspectiveOptions,
): PerspectiveState {
  const { fetchImpl } = useSession();

  // 1. Health / share / fallback. `trafficClass: 'tenant'` excludes internal
  //    probe traffic — a probe storm must not colour the map an operator uses
  //    to judge what tenants are experiencing.
  const traffic = useOpsTrafficOverview({ window, trafficClass: 'tenant' }, options);

  // The bounds every money read is driven with. Undefined until (1) answers.
  const bounds = traffic.data?.window;
  const from = bounds?.from;
  const to = bounds?.to;
  const moneyEnabled = from !== undefined && to !== undefined;

  // `brand: 'all'` — the fleet map is not brand-scoped; splitting by brand
  // here would hide half the tenants behind a filter the page never exposes.
  const businessQuery: BusinessQuery = useMemo(
    () => ({ window: 'custom', brand: 'all', from: from ?? '', to: to ?? '' }),
    [from, to],
  );
  const revenueQuery: RevenueByUpstreamQuery = useMemo(
    () => ({ from: from ?? '', to: to ?? '' }),
    [from, to],
  );

  // 2/3. No `refetchInterval` of their own: their query keys carry the traffic
  //      window's bounds, so a traffic poll that moves the window re-keys them
  //      and they refetch as a consequence. Polling them independently would
  //      let the money layer land on a window the health layer has not caught
  //      up to, which is the exact overlay this page must never render.
  const skuEconomics = useQuery({
    queryKey: BUSINESS_QUERY_KEYS.page('sku-economics', businessQuery),
    queryFn: ({ signal }) => getBusinessPage('sku-economics', businessQuery, fetchImpl, signal),
    enabled: moneyEnabled,
  });
  const upstreamRevenue = useQuery({
    queryKey: REVENUE_QUERY_KEYS.byUpstream(revenueQuery),
    queryFn: ({ signal }) => getRevenueByUpstream(revenueQuery, fetchImpl, signal),
    enabled: moneyEnabled,
  });

  // 4/5. The edges come from the global default config, because that is the
  //      routing every org resolves to unless it carries its own assignment —
  //      the fleet-wide picture the map claims to draw. One bulk sku-summary
  //      read covers every SKU's targets and policies; the per-SKU endpoints
  //      would be one request per catalog SKU.
  // 6. The supplier names an operator actually reasons about — `dragoncode`,
  //    `llmport`, `ucloud`. Nothing else can supply them:
  //    `OpsTrafficBreakdownRow.upstreamName` is contractual but populated by
  //    neither the gateway projection nor ops-api's `decorateRow`. Its own
  //    slice, and a failure degrades every name to a channel slug rather than
  //    taking the page down.
  const upstreams = useUpstreamsList();

  const configs = useRoutingConfigs();
  const globalDefaultConfigId =
    configs.data?.configs.find((config) => config.is_global_default)?.id ?? null;
  const routing = useRoutingConfigSkuSummary(globalDefaultConfigId);

  const failures = useMemo<readonly SliceFailure[]>(
    () =>
      [
        failureFrom('traffic', traffic.error),
        failureFrom('money-sku', skuEconomics.error),
        failureFrom('money-upstream', upstreamRevenue.error),
        // Both routing reads degrade the same layer: without the config list
        // there is no id to summarize, so a failure in either leaves the page
        // with no edges and must say so once.
        failureFrom('routing', configs.error ?? routing.error),
        failureFrom('upstreams', upstreams.error),
      ].filter((failure): failure is SliceFailure => failure !== null),
    [
      traffic.error,
      skuEconomics.error,
      upstreamRevenue.error,
      configs.error,
      routing.error,
      upstreams.error,
    ],
  );

  // The model appears as soon as the traffic query has SETTLED, success or
  // failure. On failure it is built with `traffic: null`, which grades every
  // node `unknown` — a rendered map that admits it cannot see is more useful
  // than a spinner that never resolves.
  const trafficSettled = traffic.isSuccess || traffic.isError;

  // `data ?? null` rather than `isSuccess ? data : null`: React Query keeps
  // the last good payload when a background refetch fails, and a stale map
  // plus a visible failure beats blanking a working page on one bad poll.
  const model = useMemo<PerspectiveModel | null>(
    () =>
      trafficSettled
        ? buildPerspectiveModel({
            window,
            traffic: traffic.data ?? null,
            skuEconomics: skuEconomics.data ?? null,
            upstreamRevenue: upstreamRevenue.data ?? null,
            routing: routing.data ?? null,
            upstreams: upstreams.data ?? null,
            failures,
          })
        : null,
    [
      trafficSettled,
      window,
      traffic.data,
      skuEconomics.data,
      upstreamRevenue.data,
      routing.data,
      upstreams.data,
      failures,
    ],
  );

  const refetchTraffic = traffic.refetch;
  const refetchSkuEconomics = skuEconomics.refetch;
  const refetchUpstreamRevenue = upstreamRevenue.refetch;
  const refetchConfigs = configs.refetch;
  const refetchRouting = routing.refetch;
  const refetchUpstreams = upstreams.refetch;
  const refetch = useCallback(() => {
    void refetchTraffic();
    void refetchSkuEconomics();
    void refetchUpstreamRevenue();
    void refetchConfigs();
    void refetchRouting();
    void refetchUpstreams();
  }, [
    refetchTraffic,
    refetchSkuEconomics,
    refetchUpstreamRevenue,
    refetchConfigs,
    refetchRouting,
    refetchUpstreams,
  ]);

  return {
    model,
    isLoading: model === null,
    isFetching:
      traffic.isFetching ||
      skuEconomics.isFetching ||
      upstreamRevenue.isFetching ||
      configs.isFetching ||
      routing.isFetching ||
      upstreams.isFetching,
    error: traffic.error,
    refetch,
  };
}
